import React, { useState } from "react";
import { ChevronDown, ChevronUp, ExternalLink } from "lucide-react";

// Все расчёты — чистые функции, без обращения к серверу. Формулы даны со
// ссылкой на источник в комментарии, чтобы при пересмотре методики было
// понятно, что именно и откуда обновлять.

// ИМТ (индекс массы тела)
function bmiResult(weightKg, heightCm) {
  const h = heightCm / 100;
  const bmi = weightKg / (h * h);
  let category;
  if (bmi < 16) category = "Выраженный дефицит массы тела";
  else if (bmi < 18.5) category = "Дефицит массы тела";
  else if (bmi < 25) category = "Норма";
  else if (bmi < 30) category = "Избыточная масса тела";
  else if (bmi < 35) category = "Ожирение I степени";
  else if (bmi < 40) category = "Ожирение II степени";
  else category = "Ожирение III степени";
  return { value: bmi.toFixed(1), category };
}

// СКФ по CKD-EPI 2021 (без поправки на расу — актуальная версия формулы,
// National Kidney Foundation / ASN Task Force, 2021)
// Креатинин в крови обычно указывается в мкмоль/л (РФ) — переводим в мг/дл (/88.4)
function ckdEpi2021(creatinineUmolL, age, isFemale) {
  const scr = creatinineUmolL / 88.4; // мг/дл
  const kappa = isFemale ? 0.7 : 0.9;
  const alpha = isFemale ? -0.241 : -0.302;
  const minRatio = Math.min(scr / kappa, 1);
  const maxRatio = Math.max(scr / kappa, 1);
  let egfr = 142 * Math.pow(minRatio, alpha) * Math.pow(maxRatio, -1.2) * Math.pow(0.9938, age);
  if (isFemale) egfr *= 1.012;
  let stage;
  if (egfr >= 90) stage = "C1 — норма или высокая";
  else if (egfr >= 60) stage = "C2 — незначительно снижена";
  else if (egfr >= 45) stage = "C3а — умеренно снижена";
  else if (egfr >= 30) stage = "C3б — существенно снижена";
  else if (egfr >= 15) stage = "C4 — резко снижена";
  else stage = "C5 — терминальная почечная недостаточность";
  return { value: egfr.toFixed(0), stage };
}

// CHA2DS2-VASc — риск инсульта при фибрилляции предсердий
// (2020 ESC Guidelines for AF)
const CHA2DS2_ITEMS = [
  { key: "chf", label: "ХСН / систолическая дисфункция ЛЖ", points: 1 },
  { key: "htn", label: "Артериальная гипертензия", points: 1 },
  { key: "age75", label: "Возраст ≥ 75 лет", points: 2 },
  { key: "dm", label: "Сахарный диабет", points: 1 },
  { key: "stroke", label: "Инсульт / ТИА / тромбоэмболия в анамнезе", points: 2 },
  { key: "vasc", label: "Сосудистое заболевание (ИМ, атеросклероз периф. артерий, бляшка аорты)", points: 1 },
  { key: "age65", label: "Возраст 65–74 года", points: 1 },
  { key: "female", label: "Женский пол", points: 1 },
];

function cha2ds2vascResult(checked) {
  const score = CHA2DS2_ITEMS.reduce((sum, item) => sum + (checked[item.key] ? item.points : 0), 0);
  let risk;
  if (score === 0) risk = "Очень низкий — антикоагулянты не показаны";
  else if (score === 1) risk = "Низкий — антикоагулянты рассмотреть индивидуально";
  else risk = "Высокий — антикоагулянтная терапия показана";
  return { score, risk };
}

// HAS-BLED — риск кровотечения на фоне антикоагулянтной терапии
const HASBLED_ITEMS = [
  { key: "htn", label: "Неконтролируемая АГ (САД > 160 мм рт. ст.)", points: 1 },
  { key: "renal", label: "Нарушение функции почек (диализ, трансплантация, креатинин > 200 мкмоль/л)", points: 1 },
  { key: "liver", label: "Нарушение функции печени (цирроз, билирубин ×2, АСТ/АЛТ/ЩФ ×3 от нормы)", points: 1 },
  { key: "stroke", label: "Инсульт в анамнезе", points: 1 },
  { key: "bleeding", label: "Кровотечение в анамнезе или предрасположенность к нему", points: 1 },
  { key: "inr", label: "Лабильное МНО (нестабильное, часто вне терапевтического диапазона)", points: 1 },
  { key: "elderly", label: "Возраст > 65 лет", points: 1 },
  { key: "drugs", label: "Антиагреганты / НПВС", points: 1 },
  { key: "alcohol", label: "Злоупотребление алкоголем (≥ 8 доз/нед.)", points: 1 },
];

function hasBledResult(checked) {
  const score = HASBLED_ITEMS.reduce((sum, item) => sum + (checked[item.key] ? item.points : 0), 0);
  const risk = score >= 3 ? "Высокий риск кровотечения — требуется коррекция модифицируемых факторов и более частое наблюдение" : "Низкий/умеренный риск";
  return { score, risk };
}

// Wells score для ТЭЛА (оригинальная версия, используется в РФ наравне с
// упрощённой — трёхуровневая интерпретация по Wells 2000/2001)
const WELLS_PE_ITEMS = [
  { key: "dvt", label: "Клинические признаки ТГВ (отёк, боль при пальпации по ходу вен)", points: 3 },
  { key: "altDx", label: "ТЭЛА — наиболее вероятный диагноз", points: 3 },
  { key: "hr", label: "ЧСС > 100 уд/мин", points: 1.5 },
  { key: "immob", label: "Иммобилизация ≥ 3 дней или операция за последние 4 недели", points: 1.5 },
  { key: "prevPe", label: "ТГВ/ТЭЛА в анамнезе", points: 1.5 },
  { key: "hemoptysis", label: "Кровохарканье", points: 1 },
  { key: "malignancy", label: "Онкологическое заболевание (леченное в течение 6 мес. или паллиативное)", points: 1 },
];

function wellsPeResult(checked) {
  const score = WELLS_PE_ITEMS.reduce((sum, item) => sum + (checked[item.key] ? item.points : 0), 0);
  let risk;
  if (score <= 1) risk = "Низкая вероятность ТЭЛА";
  else if (score <= 6) risk = "Умеренная вероятность ТЭЛА";
  else risk = "Высокая вероятность ТЭЛА";
  return { score, risk };
}

// CURB-65 — тяжесть внебольничной пневмонии, показание к госпитализации
// (British Thoracic Society)
const CURB65_ITEMS = [
  { key: "confusion", label: "Спутанность сознания (новая, дезориентация)", points: 1 },
  { key: "urea", label: "Мочевина > 7 ммоль/л", points: 1 },
  { key: "rr", label: "ЧДД ≥ 30/мин", points: 1 },
  { key: "bp", label: "АД: систолическое < 90 или диастолическое ≤ 60 мм рт. ст.", points: 1 },
  { key: "age", label: "Возраст ≥ 65 лет", points: 1 },
];

function curb65Result(checked) {
  const score = CURB65_ITEMS.reduce((sum, item) => sum + (checked[item.key] ? item.points : 0), 0);
  let risk;
  if (score <= 1) risk = "Низкий риск — возможно амбулаторное лечение";
  else if (score === 2) risk = "Средний риск — рассмотреть госпитализацию (в т.ч. краткосрочную/дневной стационар)";
  else risk = "Высокий риск — госпитализация, рассмотреть ОРИТ при 4–5 баллах";
  return { score, risk };
}

// TIMI risk score для нестабильной стенокардии / ИМ без подъёма ST
// (Antman et al., JAMA 2000) — 7 бинарных критериев, не путать с TIMI для
// STEMI (другая, невзвешенная по баллам шкала — здесь не реализована)
const TIMI_NSTEMI_ITEMS = [
  { key: "age65", label: "Возраст ≥ 65 лет", points: 1 },
  { key: "riskFactors", label: "≥ 3 факторов риска ИБС (семейный анамнез, АГ, гиперхолестеринемия, СД, курение)", points: 1 },
  { key: "knownCad", label: "Известный стеноз коронарной артерии ≥ 50%", points: 1 },
  { key: "aspirin", label: "Приём аспирина в последние 7 дней", points: 1 },
  { key: "angina", label: "Тяжёлая стенокардия — ≥ 2 приступов за последние 24 часа", points: 1 },
  { key: "stDev", label: "Отклонение сегмента ST ≥ 0.5 мм на ЭКГ", points: 1 },
  { key: "markers", label: "Повышенные кардиоспецифичные маркеры (тропонин/КФК-МВ)", points: 1 },
];

// Риск смерти/ИМ/срочной реваскуляризации за 14 дней по баллу — точные
// значения из исходной публикации (Antman et al., JAMA 2000, TIMI 11B/ESSENCE)
const TIMI_NSTEMI_RISK_BY_SCORE = { 0: 4.7, 1: 4.7, 2: 8.3, 3: 13.2, 4: 19.9, 5: 26.2, 6: 40.9, 7: 40.9 };

function timiNstemiResult(checked) {
  const score = TIMI_NSTEMI_ITEMS.reduce((sum, item) => sum + (checked[item.key] ? item.points : 0), 0);
  const pct = TIMI_NSTEMI_RISK_BY_SCORE[score];
  const risk = score <= 2 ? "Низкий риск" : score <= 4 ? "Средний риск" : "Высокий риск";
  return { score, risk, pct };
}

function CalculatorCard({ title, subtitle, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card">
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left",
        }}
      >
        <div>
          <h3 style={{ color: "#007c84", margin: 0 }}>{title}</h3>
          {subtitle && <p style={{ color: "#777", fontSize: "0.85rem", margin: "4px 0 0" }}>{subtitle}</p>}
        </div>
        {open ? <ChevronUp size={20} color="#007c84" /> : <ChevronDown size={20} color="#007c84" />}
      </button>
      {open && <div style={{ marginTop: 16 }}>{children}</div>}
    </div>
  );
}

function ResultBox({ children }) {
  return (
    <div style={{
      marginTop: 14, padding: "10px 14px", background: "#f0f4ff", borderRadius: 8,
      borderLeft: "3px solid #007c84", fontSize: "0.95rem",
    }}>
      {children}
    </div>
  );
}

function NumberField({ label, value, onChange, ...rest }) {
  return (
    <label style={{ display: "block", marginBottom: 10, fontSize: "0.9rem", color: "#444" }}>
      {label}
      <input
        type="number"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ display: "block", width: "100%", marginTop: 4, padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc" }}
        {...rest}
      />
    </label>
  );
}

function CheckboxList({ items, checked, onToggle }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {items.map(item => (
        <label key={item.key} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: "0.9rem", color: "#333", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={!!checked[item.key]}
            onChange={() => onToggle(item.key)}
            style={{ marginTop: 3, flexShrink: 0 }}
          />
          <span>{item.label} <span style={{ color: "#999" }}>(+{item.points})</span></span>
        </label>
      ))}
    </div>
  );
}

function BmiCalculator() {
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const result = weight && height ? bmiResult(parseFloat(weight), parseFloat(height)) : null;
  return (
    <CalculatorCard title="Индекс массы тела (ИМТ)" subtitle="Оценка соответствия массы тела росту">
      <NumberField label="Масса тела, кг" value={weight} onChange={setWeight} min="0" step="0.1" />
      <NumberField label="Рост, см" value={height} onChange={setHeight} min="0" step="0.1" />
      {result && (
        <ResultBox>
          <strong>ИМТ: {result.value} кг/м²</strong> — {result.category}
        </ResultBox>
      )}
    </CalculatorCard>
  );
}

function CkdEpiCalculator() {
  const [creatinine, setCreatinine] = useState("");
  const [age, setAge] = useState("");
  const [isFemale, setIsFemale] = useState(false);
  const result = creatinine && age ? ckdEpi2021(parseFloat(creatinine), parseFloat(age), isFemale) : null;
  return (
    <CalculatorCard title="СКФ (CKD-EPI 2021)" subtitle="Расчётная скорость клубочковой фильтрации по креатинину">
      <NumberField label="Креатинин сыворотки, мкмоль/л" value={creatinine} onChange={setCreatinine} min="0" step="1" />
      <NumberField label="Возраст, лет" value={age} onChange={setAge} min="0" step="1" />
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.9rem", marginTop: 4 }}>
        <input type="checkbox" checked={isFemale} onChange={e => setIsFemale(e.target.checked)} />
        Женский пол
      </label>
      {result && (
        <ResultBox>
          <strong>СКФ: {result.value} мл/мин/1.73м²</strong> — {result.stage}
        </ResultBox>
      )}
    </CalculatorCard>
  );
}

function Cha2ds2VascCalculator() {
  const [checked, setChecked] = useState({});
  const toggle = key => setChecked(c => ({ ...c, [key]: !c[key] }));
  const result = cha2ds2vascResult(checked);
  return (
    <CalculatorCard title="CHA₂DS₂-VASc" subtitle="Риск инсульта при фибрилляции предсердий">
      <CheckboxList items={CHA2DS2_ITEMS} checked={checked} onToggle={toggle} />
      <ResultBox>
        <strong>Сумма баллов: {result.score}</strong> — {result.risk}
      </ResultBox>
    </CalculatorCard>
  );
}

function HasBledCalculator() {
  const [checked, setChecked] = useState({});
  const toggle = key => setChecked(c => ({ ...c, [key]: !c[key] }));
  const result = hasBledResult(checked);
  return (
    <CalculatorCard title="HAS-BLED" subtitle="Риск кровотечения на фоне антикоагулянтной терапии">
      <CheckboxList items={HASBLED_ITEMS} checked={checked} onToggle={toggle} />
      <ResultBox>
        <strong>Сумма баллов: {result.score}</strong> — {result.risk}
      </ResultBox>
    </CalculatorCard>
  );
}

function WellsPeCalculator() {
  const [checked, setChecked] = useState({});
  const toggle = key => setChecked(c => ({ ...c, [key]: !c[key] }));
  const result = wellsPeResult(checked);
  return (
    <CalculatorCard title="Шкала Wells (ТЭЛА)" subtitle="Клиническая вероятность тромбоэмболии лёгочной артерии">
      <CheckboxList items={WELLS_PE_ITEMS} checked={checked} onToggle={toggle} />
      <ResultBox>
        <strong>Сумма баллов: {result.score}</strong> — {result.risk}
      </ResultBox>
    </CalculatorCard>
  );
}

function Curb65Calculator() {
  const [checked, setChecked] = useState({});
  const toggle = key => setChecked(c => ({ ...c, [key]: !c[key] }));
  const result = curb65Result(checked);
  return (
    <CalculatorCard title="CURB-65" subtitle="Тяжесть внебольничной пневмонии, показание к госпитализации">
      <CheckboxList items={CURB65_ITEMS} checked={checked} onToggle={toggle} />
      <ResultBox>
        <strong>Сумма баллов: {result.score}</strong> — {result.risk}
      </ResultBox>
    </CalculatorCard>
  );
}

function TimiNstemiCalculator() {
  const [checked, setChecked] = useState({});
  const toggle = key => setChecked(c => ({ ...c, [key]: !c[key] }));
  const result = timiNstemiResult(checked);
  return (
    <CalculatorCard title="TIMI (НС/ИМбпST)" subtitle="Риск для нестабильной стенокардии и ИМ без подъёма ST">
      <CheckboxList items={TIMI_NSTEMI_ITEMS} checked={checked} onToggle={toggle} />
      <ResultBox>
        <strong>Сумма баллов: {result.score}</strong> — {result.risk}, риск смерти/ИМ/срочной реваскуляризации за 14 дней ≈ {result.pct}%
      </ResultBox>
    </CalculatorCard>
  );
}

function GraceExternalCard() {
  return (
    <CalculatorCard title="GRACE" subtitle="Риск смерти при остром коронарном синдроме">
      <p style={{ color: "#555", fontSize: "0.9rem", marginTop: 0 }}>
        GRACE 2.0 — нелинейная модель (не сумма баллов, как остальные шкалы здесь),
        официальные коэффициенты не публикуются в виде простой таблицы. Чтобы не
        рисковать точностью расчёта смертности, встраивать собственную формулу не стали —
        пользуйтесь официальным калькулятором:
      </p>
      <a href="https://www.mdcalc.com/calc/1099/grace-acs-risk-mortality-calculator"
        target="_blank" rel="noopener noreferrer"
        style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#007c84", fontWeight: 700, fontSize: "0.92rem" }}>
        Открыть GRACE ACS Risk Calculator (MDCalc) <ExternalLink size={14} />
      </a>
    </CalculatorCard>
  );
}

export default function Calculators() {
  return (
    <div>
      <h3 style={{ color: "#007c84", marginBottom: 4 }}>Калькуляторы и шкалы</h3>
      <p style={{ color: "#777", fontSize: "0.9rem", marginTop: 0, marginBottom: 16 }}>
        Справочные расчёты, не заменяют клиническое суждение врача.
      </p>
      <BmiCalculator />
      <CkdEpiCalculator />
      <Cha2ds2VascCalculator />
      <HasBledCalculator />
      <WellsPeCalculator />
      <Curb65Calculator />
      <TimiNstemiCalculator />
      <GraceExternalCard />
    </div>
  );
}
